import { useAppNotificationManager } from "@/components/AppProvider/AppNotificationProvider";
import { translation } from "@/i18n";
import { nsVersion } from "@/i18n/locales/version";
import React, { useMemo } from "react";
import { useNavigate } from "react-router";
import { useAppRouteParams } from "../../../../../AppRoutes";
import { postRemoveVersion } from "../../../../../api/version";
import { Version } from "../../../../../domain/Version";
import { DialogPhaseStatesType, useDialog } from "../../../helpers/hooks";

const useTranslation = translation.use(nsVersion);

export const DialogVersionRemove: React.FC<
  {
    versions: Version[];
    selectedVersionIds: string[];
    setSelectedVersionIds: React.Dispatch<React.SetStateAction<string[]>>;
  } & DialogPhaseStatesType
> = ({
  versions,
  selectedVersionIds,
  setSelectedVersionIds,
  dialogPhase,
  setDialogPhase,
}) => {
  const { t } = useTranslation();
  const { organizationId, versionId } = useAppRouteParams();
  const navigate = useNavigate();

  const { renderDialogs } = useDialog({
    dialogPhase,
    setDialogPhase,
  });

  const { showSuccessNotification, showErrorNotification } =
    useAppNotificationManager();

  const runRemove = async () => {
    const isDeletingCurrentVersion = selectedVersionIds.includes(versionId);

    setDialogPhase("in_progress");
    try {
      await postRemoveVersion({
        organizationId,
        versionId: selectedVersionIds[0],
      });
      setSelectedVersionIds([]);
      showSuccessNotification(t.version.removeDialog.success());

      // 現在表示中のバージョンを削除した場合は組織ページに遷移
      if (isDeletingCurrentVersion) {
        navigate(`/organizations/${organizationId}`);
      }
    } catch (error) {
      showErrorNotification(t.version.removeDialog.error(), error);
    }
    setDialogPhase(null);
  };

  const selectedVersion = useMemo(
    () => versions.find(({ id }) => selectedVersionIds?.[0] === id),
    [versions, selectedVersionIds]
  );

  return (
    <>
      {renderDialogs({
        confirm: {
          title: t.version.removeDialog.title(),
          displayMessage: t.version.removeDialog.message({
            versionName: selectedVersion?.title || t.version.fallbackName(),
          }),
          element: (
            <div
              style={{
                color: "var(--figma-color-status-error)",
                marginTop: 8,
              }}
            >
              {t.version.removeDialog.warning()}
            </div>
          ),
          buttonText: t.version.removeDialog.button(),
          inProgressButtonText: t.version.removeDialog.inProgressButton(),
          onClick: runRemove,
          onClickCancel: () => {
            setSelectedVersionIds([]);
          },
          isThemeRed: true,
        },
      })}
    </>
  );
};
